/**
 * Idempotent loader for the Google Maps JavaScript API (Places library).
 *
 * Resolves with `window.google.maps.places` when the script is ready, or `null`
 * if no `REACT_APP_GOOGLE_MAPS_API_KEY` is configured (caller should fall back
 * to the manual address fields and browser autocomplete).
 *
 * Usage:
 *   const places = await loadGooglePlaces();
 *   if (!places) return; // env var missing — silently degrade
 *   const ac = new places.Autocomplete(inputEl, { ... });
 */

let loadPromise = null;

const SCRIPT_ID = 'rs-google-maps-places-script';

export function loadGooglePlaces() {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps.places);
  }

  if (loadPromise) return loadPromise;

  const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();
  if (!apiKey) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[loadGooglePlaces] REACT_APP_GOOGLE_MAPS_API_KEY missing — address autofill disabled'
      );
    }
    return Promise.resolve(null);
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const onReady = () => {
      if (window.google?.maps?.places) {
        resolve(window.google.maps.places);
      } else {
        reject(new Error('Google Maps loaded but window.google.maps.places is missing'));
      }
    };

    if (existing) {
      existing.addEventListener('load', onReady, { once: true });
      existing.addEventListener('error', () => reject(new Error('Google Maps script failed to load')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&v=weekly`;
    script.addEventListener('load', onReady, { once: true });
    script.addEventListener('error', () => {
      loadPromise = null;
      reject(new Error('Google Maps script failed to load'));
    }, { once: true });
    document.head.appendChild(script);
  }).catch((err) => {
    loadPromise = null;
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[loadGooglePlaces] failed:', err.message);
    }
    return null;
  });

  return loadPromise;
}

export default loadGooglePlaces;
